import * as Yup from "yup";
import React, { useEffect, useState } from "react";
import eventClientApi from "@/api-client/event-client";
import Box from "@/components/ui/box";
import { Input } from "@/components/ui/input";
import SectionHeader from "@/components/ui/section-header";
import { Text } from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { ArrowLeft2, Camera, Trash, SearchNormal1, Add } from "iconsax-react";
import { FileButton } from "@/components/ui/file-button";
import { uploadFile } from "@/lib/uploadthing";
import { EndPoints } from "@/types/uploadthing";
import { toast } from "sonner";
import { useFormik } from "formik";
import { DEFAULT_IMAGE_URL, getChangedFields } from "@/lib/utils";
import { Loader } from "@/components/ui/loader";
import GroupUserList from "./group-user-edit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useGuestlist } from "@/hooks/use-event-group-participants";
import { useInView } from "react-intersection-observer";
import useDebounce from "@/hooks/use-debounce";
import { X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

interface CreateGroupProps {
  mode: "create" | "edit";
  setMode: React.Dispatch<React.SetStateAction<boolean>>;
  selectedGroup?: any;
  setShowParticipantsBar?: any;
  setAddMember: React.Dispatch<React.SetStateAction<boolean>>;
  addMember?: boolean;
  refetch: () => void;
  setSelectedGroup: React.Dispatch<React.SetStateAction<any>>;
  setOpened: React.Dispatch<React.SetStateAction<string>>;
}
const Schema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  description: Yup.string().required("Description is required"),
  image: Yup.string().nullable()
});

const CreateGroup = ({
  mode,
  setMode,
  selectedGroup,
  setShowParticipantsBar,
  setAddMember,
  addMember,
  refetch,
  setSelectedGroup,
  setOpened
}: CreateGroupProps) => {
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isdelete, setIsDelete] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const finalSearchValue = useDebounce(search, 300);
  const {
    data: groupParticipants,
    fetchNextPage: fetchNextGroupParticipants,
    hasNextPage: hasNextGroupParticipants,
    isLoading: loadingGroupParticipants,
    isFetchingNextPage: isFetchingNextGroupParticipants,
    refetch: refetchGroupParticipants,
    isFetching
  } = useGuestlist({
    groupId: selectedGroup.id,
    search: finalSearchValue
  });

  const [allGroupsRef, allCalInView] = useInView({
    threshold: 0.01
  });
  useEffect(() => {
    if (allCalInView && !isFetchingNextGroupParticipants && !loadingGroupParticipants)
      fetchNextGroupParticipants();
  }, [
    allCalInView,
    fetchNextGroupParticipants,
    isFetchingNextGroupParticipants,
    loadingGroupParticipants
  ]);
  const initialValues =
    mode === "edit"
      ? selectedGroup
      : {
          name: "",
          description: "",
          image: DEFAULT_IMAGE_URL
        };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Schema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const changedFields = mode === "edit" ? getChangedFields(values, initialValues) : values;
        if (mode === "edit" && changedFields && Object.keys(changedFields).length === 0) {
          setMode(false);
          return;
        }
        let response;
        if (mode === "create") {
          response = await eventClientApi.post(`api/v1/groups`, { json: values }).json<any>();
        } else {
          response = await eventClientApi
            .patch(`api/v1/groups/${selectedGroup?.id}`, { json: changedFields })
            .json<any>();
        }

        if (response.status === 200 || response.status === 201) {
          await refetch();
          setMode(false);
          setShowParticipantsBar && setShowParticipantsBar(true);
          toast.success(
            mode === "create" ? "Group created successfully" : "Group updated successfully"
          );
        }
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    }
  });

  const handleLogo = async (file: File | null) => {
    if (!file) return;
    setImageLoading(true);
    try {
      const { url } = await uploadFile(file as any, EndPoints.eventBanner);
      formik.setFieldValue("image", url, false);
    } catch (err) {
      toast.error("Something went wrong while uploading image.");
    } finally {
      setImageLoading(false);
    }
  };

  const handleDeleteUserFromGroup = async (userId: string) => {
    try {
      const payload = { guestIds: [userId] };
      setDeletingUserId(userId);
      const response = await eventClientApi
        .post(`api/v1/groups/${selectedGroup.id}/guests/delete`, {
          json: payload
        })
        .json<any>();
      refetchGroupParticipants();
      toast.success(response?.message || "User removed from group successfully.");
    } catch (error) {
      toast.error(error?.response || "Failed to remove user from group.");
    }
    setDeletingUserId(null);
  };

  const deleteGroup = async () => {
    setIsDeleting(true);
    try {
      const resp = await eventClientApi
        .post(`api/v1/groups/delete`, { json: { groupIds: [selectedGroup?.id] } })
        .json<any>();
      toast.success(resp?.message || "Group deleted successfully");
      refetch();
      setMode(false);
      setSelectedGroup({});
      setOpened("");
    } catch (error) {
      toast.error(error?.message || "Something went wrong while deleting group.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="h-[calc(100dvh-80px)] overflow-auto">
      <SectionHeader
        containerClassName="my-s px-s"
        titleText={
          <Text variant="title_three" className="font-semibold flex items-center">
            <ArrowLeft2
              className="mr-m cursor-pointer"
              onClick={() => {
                setMode(false);
                setShowParticipantsBar && setShowParticipantsBar(true);
              }}
            />
            {mode === "edit" ? "Edit group" : "Create group"}
          </Text>
        }
        action={
          mode === "edit" ? (
            <Trash color="#CD3333" className="cursor-pointer" onClick={() => setIsDelete(true)} />
          ) : (
            <></>
          )
        }
      />

      <div className="border-b border-gray-200 w-full"></div>
      <Box className="w-full">
        <div className="mx-auto flex justify-center cursor-pointer">
          <FileButton onChange={handleLogo} accept="image/png,image/jpeg" disabled={imageLoading}>
            {(props) => (
              <div className="relative group" {...props}>
                <img
                  src={formik.values.image || DEFAULT_IMAGE_URL}
                  alt="user-profile"
                  className="rounded-8 object-cover h-[160px] w-[160px]"
                />
                {imageLoading ? (
                  <Loader
                    size="medium"
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                  />
                ) : (
                  <div className="flex z-10 justify-center opacity-0 items-center rounded-8 absolute top-0 bottom-0 backdrop-blur-10 h-[160px] w-[160px] bg-[#00000059] group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex flex-col justify-center items-center">
                      <div className="w-9 h-9 flex justify-center items-center bg-container p-s-nudge rounded-small border-container-border">
                        <Camera size={16} />
                      </div>
                      <Text className="text-body-2-strong text-white w-[116px] text-center">
                        Change Group Picture
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            )}
          </FileButton>
        </div>
      </Box>

      <div className="px-m">
        <Text variant="body_one">Name</Text>
        <Input
          variant="medium-s"
          className="rounded-4 w-full px-xl"
          inputClassName="w-full"
          placeholder="Name"
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          errorMessage={formik.errors.name}
          error={(formik.errors.name && formik.touched.name) as boolean}
        />

        <Text variant="body_one" className="mt-xl">
          Description
        </Text>
        {/* <Input
          variant="medium-s"
          className="rounded-4 w-full px-xl"
          inputClassName="w-full"
          placeholder="Description"
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
          errorMessage={formik.errors.description}
          error={!!formik.errors.description}
        /> */}
        <div className="relative w-full">
          <Textarea
            name="description"
            className="rounded-4 w-full"
            placeholder="Description"
            maxLength={250}
            rows={3}
            value={formik.values.description}
            onChange={formik.handleChange}
            errorMessage={formik.errors.description}
            error={(formik.errors.description && formik.touched.description) as boolean}
          />
          <span className="absolute bottom-2 right-2 text-xs text-[#B2B2B2] pointer-events-none">
            {formik.values.description.length}/{250}
          </span>
        </div>
      </div>
      <div className="sticky top-0 z-10 bg-white">
        {mode === "edit" ? (
          <div className="bg-[#F5F6F9] mt-4 mb-1 py-[10px] px-4 w-full">
            <Text variant={"body_one"}> {groupParticipants?.pages[0]?.count} Members </Text>
          </div>
        ) : null}

        <div className="px-l flex justify-between w-full gap-l py-l items-center">
          {/* <Text className=" font-semibold mb-4" variant={"body_one"}>
          Group users
        </Text> */}
          {mode === "edit" ? (
            <>
              <div className=" w-full">
                <Input
                  variant="medium-s"
                  className="rounded-xs !w-full px-l bg-[#F5F6F9]"
                  inputClassName="w-full"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  leftSectionBgClassName="bg-transparent !mr-2 items-center content-center p-none m-auto "
                  leftSection={<SearchNormal1 size="24" variant="Outline" />}
                  rightSectionClassName="py-none px-none pr-m-nudge"
                  rightSection={
                    search && (
                      <X
                        size={20}
                        onClick={() => {
                          setSearch("");
                        }}
                      ></X>
                    )
                  }
                />
              </div>

              <Button size={"lg"} variant={"primary"} onClick={() => setAddMember(true)}>
                <Add size={24} className="mr-1" /> Add
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {mode === "edit" ? (
        <GroupUserList
          users={groupParticipants}
          isLoading={loadingGroupParticipants}
          onDeleteUser={handleDeleteUserFromGroup}
          fetchNext={fetchNextGroupParticipants}
          hasNextPage={hasNextGroupParticipants}
          isFetchingNextPage={isFetchingNextGroupParticipants}
          isFetching={isFetching}
          deletingUserId={deletingUserId || ""}
        ></GroupUserList>
      ) : (
        <></>
      )}

      <div className="flex justify-end gap-m p-l absolute left-0 bottom-0 right-0">
        <Button className="w-[150px]" variant="gradient-primary" onClick={() => setMode(false)}>
          Cancel
        </Button>
        <Button
          className="w-[150px]"
          variant="primary"
          disabled={loading || imageLoading}
          loading={loading}
          onClick={() => formik.handleSubmit()}
        >
          {mode === "edit" ? "Save" : "Create"}
        </Button>
      </div>

      <Dialog open={isdelete} onOpenChange={() => setIsDelete(false)}>
        <DialogContent
          className="max-w-full w-[90%] sm:w-[400px] p-4 !pt-[30px] overflow-visible"
          aria-labelledby="dialog-title"
        >
          <DialogHeader>
            <DialogTitle>Delete group</DialogTitle>
          </DialogHeader>
          <Text>Are you sure you want to delete this group?</Text>
          <div className="flex  justify-end items-center">
            <Button
              variant={"reject"}
              onClick={deleteGroup}
              loading={isDeleting}
              disabled={isDeleting}
              loaderProps={{ size: "xs" }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateGroup;
